// Mapas "status do domínio → rótulo pt-BR + tom semântico" consumidos pelo
// <StatusBadge> de src/components/shared. O componente só conhece tons; o
// significado de cada status mora aqui, testado, e não em cada tela.
// Tons correspondem aos tokens de src/index.css (ok/warn/bad/info/notice/…).

export type StatusTone = "neutral" | "primary" | "info" | "ok" | "warn" | "notice" | "bad";

export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

/** Parcelas (`payment_installments.status`, já passado por `effectiveInstallmentStatus`). */
export const INSTALLMENT_STATUS_META: Record<string, StatusMeta> = {
  pending: { label: "Em aberto", tone: "neutral" },
  paid: { label: "Paga", tone: "ok" },
  overdue: { label: "Vencida", tone: "bad" },
  refunded: { label: "Reembolsada", tone: "primary" },
  canceled: { label: "Cancelada", tone: "neutral" },
};

/** Épicos do modo Jira (`project_epics.status`). */
export const EPIC_STATUS_META: Record<"active" | "done" | "archived", StatusMeta> = {
  active: { label: "Ativo", tone: "info" },
  done: { label: "Concluído", tone: "ok" },
  archived: { label: "Arquivado", tone: "neutral" },
};

export type EpicStatusKey = keyof typeof EPIC_STATUS_META;

/** Ciclo do botão de status do épico: ativo → concluído → arquivado → ativo (flows/entrega.md R8). */
export const EPIC_STATUS_CYCLE: Record<EpicStatusKey, EpicStatusKey> = {
  active: "done",
  done: "archived",
  archived: "active",
};

export const nextEpicStatus = (status: EpicStatusKey): EpicStatusKey => EPIC_STATUS_CYCLE[status];

/** Sprints do modo Jira (`project_sprints.status`). */
export const SPRINT_STATUS_META: Record<"planned" | "active" | "completed", StatusMeta> = {
  planned: { label: "Planejada", tone: "neutral" },
  active: { label: "Ativa", tone: "info" },
  completed: { label: "Concluída", tone: "ok" },
};

/** Fallback seguro para status desconhecido: mostra o código cru em tom neutro. */
export function statusMeta(map: Record<string, StatusMeta>, status: string): StatusMeta {
  return map[status] ?? { label: status, tone: "neutral" };
}

// ─── classes por tom ────────────────────────────────────────────────────────
// Tailwind precisa das classes literais no fonte (não monta `text-${tone}`).

/** Cor de texto/ícone do tom (`text-ok`, `text-bad`…). */
export const TONE_TEXT_CLASS: Record<StatusTone, string> = {
  neutral: "text-muted-foreground",
  primary: "text-primary",
  info: "text-info",
  ok: "text-ok",
  warn: "text-warn",
  notice: "text-notice",
  bad: "text-bad",
};

/** Fundo suave + texto do tom (chip, círculo numerado) — mesmo par do StatusBadge. */
export const TONE_SOFT_CLASS: Record<StatusTone, string> = {
  neutral: "bg-muted text-muted-foreground",
  primary: "bg-accent text-primary",
  info: "bg-info-soft text-info",
  ok: "bg-ok-soft text-ok",
  warn: "bg-warn-soft text-warn",
  notice: "bg-notice-soft text-notice",
  bad: "bg-bad-soft text-bad",
};

/** Fundo sólido do tom (dot de fase, coluna de kanban). */
export const TONE_BG_CLASS: Record<StatusTone, string> = {
  neutral: "bg-muted-foreground",
  primary: "bg-primary",
  info: "bg-info",
  ok: "bg-ok",
  warn: "bg-warn",
  notice: "bg-notice",
  bad: "bg-bad",
};

// ─── mais mapas de domínio ──────────────────────────────────────────────────

/** Fases do kanban de entrega (`client_projects.phase`) e colunas do board Jira. */
export const PHASE_META: Record<"backlog" | "in_progress" | "review" | "done", StatusMeta> = {
  backlog: { label: "Backlog", tone: "neutral" },
  in_progress: { label: "Em Progresso", tone: "info" },
  review: { label: "Revisão", tone: "warn" },
  done: { label: "Concluído", tone: "ok" },
};

/** Colunas do board de sprint (`project_backlog_items.status`). */
export const BACKLOG_STATUS_META: Record<"todo" | "in_progress" | "review" | "done", StatusMeta> = {
  todo: { label: "A Fazer", tone: "neutral" },
  in_progress: { label: "Em Progresso", tone: "info" },
  review: { label: "Revisão", tone: "warn" },
  done: { label: "Concluído", tone: "ok" },
};

/** Status da proposta (`budgets.status`). */
export const BUDGET_STATUS_META: Record<string, StatusMeta> = {
  draft: { label: "Rascunho", tone: "neutral" },
  sent: { label: "Enviado", tone: "info" },
  partially_signed: { label: "Aguardando cliente", tone: "notice" },
  signed: { label: "Assinado", tone: "ok" },
  accepted: { label: "Aceito", tone: "ok" },
  rejected: { label: "Rejeitado", tone: "bad" },
};

/** Resumo do orçamento no card de projeto (Entrega); `null` = sem orçamento. */
export function projectBudgetMeta(budgetStatus: string | null | undefined): StatusMeta {
  if (!budgetStatus) return { label: "Sem orçamento", tone: "bad" };
  if (budgetStatus === "accepted") return { label: "Orçamento aceito", tone: "ok" };
  if (budgetStatus === "signed") return { label: "Orçamento assinado", tone: "ok" };
  if (budgetStatus === "rejected") return { label: "Rejeitado", tone: "bad" };
  return { label: "Orçamento pendente", tone: "warn" };
}

/** Prioridade interna 0..3 (Baixa, Normal, Alta, Urgente) — projetos e backlog. */
export const PRIORITY_TONE: StatusTone[] = ["neutral", "info", "warn", "bad"];

/** Estado de projeto do Linear (`project.status.type`). */
export const LINEAR_STATE_META: Record<string, StatusMeta> = {
  backlog: { label: "Backlog", tone: "neutral" },
  planned: { label: "Planejado", tone: "info" },
  started: { label: "Em andamento", tone: "warn" },
  paused: { label: "Pausado", tone: "notice" },
  completed: { label: "Concluído", tone: "ok" },
  cancelled: { label: "Cancelado", tone: "bad" },
};

/** Prioridade do Linear 0..4 (Sem prioridade, Urgente, Alta, Média, Baixa). */
export const LINEAR_PRIORITY_TONE: Record<number, StatusTone> = {
  0: "neutral",
  1: "bad",
  2: "notice",
  3: "warn",
  4: "info",
};

/** Signatário (`budget_signatures`/`contract_signatures`), com `expired` derivado de `expires_at`. */
export const SIGNATURE_STATUS_META: Record<
  "pending" | "expired" | "signed" | "rejected",
  StatusMeta
> = {
  pending: { label: "Pendente", tone: "warn" },
  expired: { label: "Link expirado", tone: "notice" },
  signed: { label: "Assinado", tone: "ok" },
  rejected: { label: "Rejeitado", tone: "bad" },
};
